import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Mail, Phone, MapPin, Clock, MessageCircle, Warehouse } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useLang } from "@/contexts/LanguageContext";
import { CONTACT_FORM } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  subject: "",
  message: "",
};

const ContactForm = () => {
  const { t, lang } = useLang();
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (key) => (e) => setData((d) => ({ ...d, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSuccess(false);
    try {
      await axios.post(`${API}/contact`, { ...data, language: lang });
      setSuccess(true);
      setData(initial);
      toast.success(t.contact.success);
    } catch (err) {
      console.error(err);
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-pad bg-[#FAFAF8]">
      <div className="container-cust grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Info column */}
        <div className="lg:col-span-5">
          <p className="eyebrow mb-5">
            <span className="eyebrow-line"></span>
            {t.contact.eyebrow}
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1A2421] leading-tight">
            {t.contact.title}
          </h2>
          <p className="mt-6 text-[#4A5D58] text-base md:text-lg leading-relaxed">
            {t.contact.subtitle}
          </p>

          <div className="mt-12 space-y-7">
            {t.contact.addresses.map((addr, i) => (
              <InfoRow
                key={i}
                icon={i === 0 ? MapPin : Warehouse}
                label={addr.label}
                value={addr.value}
              />
            ))}
            <InfoRow
              icon={Phone}
              label={t.contact.phoneNote}
              value={t.contact.phoneValue}
              href={`tel:${t.contact.phoneValue.replace(/\s+/g, "")}`}
              extra={
                <a
                  href={`https://wa.me/${t.contact.phoneValue.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-1 text-xs text-[#0E5A4E] hover:text-[#0B4A40] underline-offset-4 hover:underline"
                  data-testid="contact-whatsapp-link"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </a>
              }
            />
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0E5A4E]/10 text-[#0E5A4E] flex-shrink-0">
                <Mail className="w-4 h-4" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-[#4A5D58] mb-3">
                  Email
                </div>
                <ul className="space-y-3">
                  {t.contact.emails.map((e) => (
                    <li key={e.value} className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3">
                      <span className="text-[11px] uppercase tracking-widest text-[#4A5D58] sm:min-w-[180px]">
                        {e.label}
                      </span>
                      <a
                        href={`mailto:${e.value}`}
                        className="text-sm md:text-base text-[#1A2421] hover:text-[#0E5A4E] break-all"
                        data-testid={`contact-email-${e.value}`}
                      >
                        {e.value}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <InfoRow icon={Clock} label={t.contact.hours} value={t.contact.hoursValue} />
          </div>
        </div>

        {/* Form column */}
        <div className="lg:col-span-7">
          <form
            data-testid={CONTACT_FORM.root}
            onSubmit={onSubmit}
            className="bg-white p-7 md:p-10 rounded-sm border border-[#E2E2DB] space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t.contact.labels.name + " *"}>
                <Input
                  data-testid={CONTACT_FORM.name}
                  required
                  value={data.name}
                  onChange={set("name")}
                  className="bg-transparent border-0 border-b border-[#E2E2DB] rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
                />
              </Field>
              <Field label={t.contact.labels.email + " *"}>
                <Input
                  data-testid={CONTACT_FORM.email}
                  required
                  type="email"
                  value={data.email}
                  onChange={set("email")}
                  className="bg-transparent border-0 border-b border-[#E2E2DB] rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
                />
              </Field>
              <Field label={t.contact.labels.company}>
                <Input
                  data-testid={CONTACT_FORM.company}
                  value={data.company}
                  onChange={set("company")}
                  className="bg-transparent border-0 border-b border-[#E2E2DB] rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
                />
              </Field>
              <Field label={t.contact.labels.phone}>
                <Input
                  data-testid={CONTACT_FORM.phone}
                  value={data.phone}
                  onChange={set("phone")}
                  className="bg-transparent border-0 border-b border-[#E2E2DB] rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
                />
              </Field>
            </div>

            <Field label={t.contact.labels.subject}>
              <Input
                data-testid={CONTACT_FORM.subject}
                value={data.subject}
                onChange={set("subject")}
                className="bg-transparent border-0 border-b border-[#E2E2DB] rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
              />
            </Field>

            <Field label={t.contact.labels.message + " *"}>
              <Textarea
                data-testid={CONTACT_FORM.message}
                required
                rows={5}
                value={data.message}
                onChange={set("message")}
                className="bg-transparent border border-[#E2E2DB] rounded-sm focus-visible:ring-0 focus-visible:border-[#0E5A4E]"
              />
            </Field>

            <div className="flex justify-end pt-2">
              <Button
                data-testid={CONTACT_FORM.submit}
                type="submit"
                disabled={submitting}
                className="bg-[#0E5A4E] hover:bg-[#0B4A40] text-white rounded-full px-7 h-12 text-sm"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t.contact.submitting}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t.contact.submit}
                  </>
                )}
              </Button>
            </div>

            {success && (
              <div
                data-testid={CONTACT_FORM.success}
                className="mt-2 p-4 bg-[#0E5A4E]/5 border border-[#0E5A4E]/20 text-[#0E5A4E] text-sm rounded-sm"
              >
                {t.contact.success}
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-2">
    <Label className="text-xs uppercase tracking-widest text-[#4A5D58]">{label}</Label>
    {children}
  </div>
);

const InfoRow = ({ icon: Icon, label, value, href, extra }) => (
  <div className="flex items-start gap-4">
    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0E5A4E]/10 text-[#0E5A4E] flex-shrink-0">
      <Icon className="w-4 h-4" />
    </span>
    <div>
      <div className="text-xs uppercase tracking-widest text-[#4A5D58] mb-1">
        {label}
      </div>
      {href ? (
        <a
          href={href}
          className="text-sm md:text-base text-[#1A2421] hover:text-[#0E5A4E] break-all"
        >
          {value}
        </a>
      ) : (
        <div className="text-sm md:text-base text-[#1A2421]">{value}</div>
      )}
      {extra}
    </div>
  </div>
);

export default ContactForm;
